/* eslint-disable no-nested-ternary */
import React, { useState, useMemo, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { Grid, Checkbox, Typography, Paper, Box } from '@material-ui/core';
import {
  Care_Plan_Problems,
  Measures,
  Progress_Notes,
  Questionnaires,
  Tasks,
  FacetoFaceEncounter,
  HCPCS_Billable,
  HopeAttribute,
} from '@mummssoftware/utils/constants';
import { DeleteVisitButtons } from './DeleteVisitButtons';
import {
  hopeId,
  facetofaceId,
  notecountId,
  manifestcountId,
  taskadmincountId,
  measurecountId,
  problemcountId,
  physicianservicesId,
} from './constants';
import { useStyles } from './style';
import defaults from '../../../translations/en.json';

interface CheckedState {
  [key: string]: boolean;
}

interface Row {
  id: string;
  description: string;
  taggedElements: string;
  deleteElement: string;
  disabled?: boolean;
}

type DeleteVisitCheckProps = {
  data: Record<string, any>;
  isHopeVisit?: boolean;
  setClosed: () => void;
  onDelete: () => void;
};

const ZERO_COUNT = 0;
const COUNT_KEYS_W_PHYS_IDS = [
  notecountId,
  manifestcountId,
  taskadmincountId,
  measurecountId,
  problemcountId,
  physicianservicesId,
] as const;

const COUNT_KEYS_IDS = [
  notecountId,
  manifestcountId,
  taskadmincountId,
  measurecountId,
  problemcountId,
] as const;

const getCountKeys = (hasPhysician: boolean | null) =>
  hasPhysician ? COUNT_KEYS_W_PHYS_IDS : COUNT_KEYS_IDS;

const hasNoTaggedItems = (count: number) => count === ZERO_COUNT;

const createConditionalRow = (
  condition: boolean,
  id: string,
  description: string,
  taggedElements: string,
  deleteElement: string,
  disabled: boolean,
): Row[] =>
  condition
    ? [{ id, description, taggedElements, deleteElement, disabled }]
    : [];

export const DeleteVisitCheck = ({
  data,
  isHopeVisit,
  setClosed,
  onDelete,
}: DeleteVisitCheckProps) => {
  const classes = useStyles();
  const intl = useIntl();

  const {
    manifestcount,
    measurecount,
    notecount,
    taskadmincount,
    problemcount,
    facetofacevisit,
    physicianservices,
  } = data;

  const [checked, setChecked] = useState<CheckedState>(() => ({
    hopeId: false,
    facetofaceId: false,
    notecountId: hasNoTaggedItems(notecount),
    manifestcountId: hasNoTaggedItems(manifestcount),
    taskadmincountId: hasNoTaggedItems(taskadmincount),
    measurecountId: hasNoTaggedItems(measurecount),
    problemcountId: hasNoTaggedItems(problemcount),
    physicianservicesId: false,
  }));

  const mapItems: Record<string, number> = useMemo(
    () => ({
      notecountId: notecount,
      manifestcountId: manifestcount,
      taskadmincountId: taskadmincount,
      measurecountId: measurecount,
      problemcountId: problemcount,
      physicianservicesId: physicianservices,
    }),
    [notecount, manifestcount, taskadmincount, measurecount, problemcount],
  );

  const cannotDeleteVisit = facetofacevisit || isHopeVisit;

  const canDeleteVisitInit = useMemo(
    () =>
      !cannotDeleteVisit &&
      hasNoTaggedItems(notecount) &&
      hasNoTaggedItems(manifestcount) &&
      hasNoTaggedItems(taskadmincount) &&
      hasNoTaggedItems(problemcount) &&
      hasNoTaggedItems(measurecount) &&
      !physicianservices,
    [
      cannotDeleteVisit,
      notecount,
      manifestcount,
      taskadmincount,
      problemcount,
      measurecount,
      physicianservices,
    ],
  );

  const getAllItemsChecked = useCallback(
    () => getCountKeys(!!physicianservices).every((key) => checked[key]),
    [checked],
  );

  const canDeleteVisit = useMemo(
    () => !cannotDeleteVisit && getAllItemsChecked(),
    [cannotDeleteVisit, getAllItemsChecked],
  );

  const rows = useMemo<Row[]>(() => {
    const hopeRow = createConditionalRow(
      !!isHopeVisit,
      hopeId,
      HopeAttribute,
      defaults.visitNoDelete,
      defaults.contactSupport,
      true,
    );

    const facetofaceRow = createConditionalRow(
      !!facetofacevisit,
      facetofaceId,
      FacetoFaceEncounter,
      defaults.visitNoDelete,
      defaults.contactSupport,
      true,
    );
    const hcpcsRow = createConditionalRow(
      !!physicianservices,
      physicianservicesId,
      HCPCS_Billable,
      '',
      defaults.deleteHCPCS,
      false,
    );

    const basicRow: Row[] = [
      {
        id: manifestcountId,
        description: Questionnaires,
        taggedElements: intl.formatMessage(defaults.formsTagged, {
          number: manifestcount,
        }),
        deleteElement: defaults.deleteForms,
      },
      {
        id: taskadmincountId,
        description: Tasks,
        taggedElements: intl.formatMessage(defaults.tasksToDelete, {
          number: taskadmincount,
        }),
        deleteElement: defaults.deleteTasks,
      },
      {
        id: notecountId,
        description: Progress_Notes,
        taggedElements: intl.formatMessage(defaults.notesTagged, {
          number: notecount,
        }),
        deleteElement: defaults.unlink,
      },
      {
        id: measurecountId,
        description: Measures,
        taggedElements: intl.formatMessage(defaults.measuresToDelete, {
          number: measurecount,
        }),
        deleteElement: defaults.unlink,
      },
      {
        id: problemcountId,
        description: Care_Plan_Problems,
        taggedElements: intl.formatMessage(defaults.cppToDelete, {
          number: problemcount,
        }),
        deleteElement: defaults.unlink,
      },
    ];

    return [...hopeRow, ...facetofaceRow, ...hcpcsRow, ...basicRow];
  }, [
    isHopeVisit,
    facetofacevisit,
    manifestcount,
    taskadmincount,
    notecount,
    measurecount,
    problemcount,
    intl,
  ]);

  const titleText = canDeleteVisitInit
    ? defaults.visitNoTagged
    : defaults.visitTagged;

  const infoText = cannotDeleteVisit
    ? defaults.noDeleteAction
    : canDeleteVisitInit
    ? defaults.deleteAction
    : defaults.pleaseReview;

  const handleChange = useCallback(
    (rowId: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const count = mapItems[rowId];
      if (count !== ZERO_COUNT) {
        setChecked((prev) => ({
          ...prev,
          [rowId]: event.target.checked,
        }));
      }
    },
    [mapItems],
  );

  return (
    <>
      <Box className={classes.root}>
        <Typography className={classes.title}>{titleText}</Typography>
        <Typography className={classes.text}>{infoText}</Typography>
        <Paper className={classes.paper}>
          {rows.map((row) => (
            <Grid
              container
              spacing={2}
              key={row.id}
              className={classes.row}
              alignItems="center"
            >
              <Grid item xs={4} className={classes.textColumn}>
                <Typography className={classes.bodyText}>
                  {row.description}
                </Typography>
              </Grid>
              <Grid item xs={4} className={classes.textColumn}>
                <Typography className={classes.bodyText}>
                  {row.taggedElements}
                </Typography>
              </Grid>
              <Grid item xs={4} className={classes.textColumn}>
                <Checkbox
                  checked={checked[row.id]}
                  onChange={handleChange(row.id)}
                  color="primary"
                  disabled={row.disabled}
                />
                <Typography className={classes.unlinkText}>
                  {row.deleteElement}
                </Typography>
              </Grid>
            </Grid>
          ))}
        </Paper>
      </Box>
      <DeleteVisitButtons
        setClosed={setClosed}
        onDelete={onDelete}
        disabled={!canDeleteVisit}
      />
    </>
  );
};
